<template>
  <div class="w-full">
    <div class="w-full bg-outage">
      <div class="py-10 lg:py-20 px-4 lg:px-24 flex flex-col">
        <h1 class="font-benzin text-white text-4xl uppercase tracking-wide font-normal mt-28">Outages</h1>
        <p class="font-poppins text-white text-xl max-w-sm">Check our current and scheduled outages here.</p>
      </div>
    </div>

    <div class="bg-white py-10 px-4 lg:px-24">
      <div class="">
        <div class="w-full flex flex-col lg:flex-row">
          <div
            class="pt-4 text-left lg:max-w-1/2 w-full"
          >
            <p class="text-lg text-black font-poppins tracking-normal pt-6 py-0 my-0 w-full max-w-lg">HERE. NOW. READY.</p>
            <p class="font-benzin text-black text-3xl tracking-normal font-normal py-0 my-0 uppercase">Major and Significant Local Outages <span class="text-brand-primary">Support</span></p>
            <p class="text-lg text-black font-poppins tracking-normal py-0 my-0">Our Support During Unplanned Major and Significant Local Outages </p>

            <div class="font-poppins text-black text-sm">
              <p class="text-sm pt-4">We understand how disruptive and frustrating unplanned major outages can be, as it impacts internet, VoIP phone services and streaming services. They are often a result of one, or a combination of, the following reasons:</p>
              <p class="text-sm pt-0">
                <ul class="list-disc ml-6">
                  <li>Network Failures – system or equipment malfunction</li>
                  <li>Fibre Cuts</li>
                  <li>Power Outages</li>
                  <li>Adverse Weather Conditions</li>
                </ul>
              </p>
              <p class="text-sm pt-0">The Telecommunications (Customer Communications for Outages) Industry Standard 2024 defines a ‘major outage’ as one that affects — or is likely to affect—100,000 operating services or all services that use a telecommunications network in an Australian state or territory and will last (or is expected to last) for more than 60 minutes.</p>
              <p class="text-sm pt-0">The Telecommunications (Customer Communications for Outages) Industry Standard 2025 defines a “significant local outage” as one that affects - or is likely to affect 1,000 or more services in regional Australia; or 250 or more services in remote Australia; and is expected to be, or is, of a duration longer than 6 hours in regional Australia or 3 hours in remote Australia.</p>
            <p class="text-sm pt-0">When Nexee becomes aware of a major unplanned outage, we will let you know via email, SMS, on our website Outages page and the <a href="https://nexee.au"><span class="underline">Customer Portal</span></a> page.</p>
            <p class="text-sm pt-0">Our outage notifications will contain at least the following information:</p>
            <p class="text-sm pt-0">
              <ol class="list-decimal ml-6">
                <li>the scale or suspected scale of the major outage;</li>
                <li>the cause or likely cause of the major outage;</li>
                <li>the geographic areas impacted or likely to be impacted by the major outage;</li>
                <li>the types of carriage services impacted or likely to be impacted by the major outage;</li>
                <li>the estimated timeframes for updates in relation to the major outage; </li>
                <li>the estimated timeframe for rectification of the major outage;</li>
                <li>contact information such as an email address, a phone number or social media account details;</li>
                <li>a website or webpage that is accessible to the public; and</li>
                <li>details of easily accessible contact channels you can use to contact us</li>
              </ol>
            </p>
          </div>
        </div>
        <div class="w-full lg:max-w-1/2 px-0 lg:px-6 py-8 justify-items-end ">
          <div class="bg-outage-note max-w-lg px-10 py-10 font-poppins text-white rounded-lg">
            <p class="text-md pt-0">Note:</p>
            <p class="text-sm pt-0">
              <ul class="list-disc ml-6">
                <li>Nexee may withhold certain information if we have reasonable grounds to believe that providing that information could compromise our network security or national security.</li>
                <li>Nexee is not required to meet its full communication obligations in the event of a
                  natural disaster.</li>
              </ul>
            </p>
            <p class="text-sm pt-0">While the outage is ongoing, we will provide real-time updates via our website’s Outages page and Customer Portal as well as via SMS and email. We will aim to provide updates every 4 hours as reasonably possible, or sooner if there is a significant development. As a minimum, we will provide updates every 6 hours, during the first 24-hour period and once during each subsequent 24-hour period</p>
            <p class="text-sm pt-0">Our Customer Service number will be updated with an acknowledgment of the outage. If you need to reach us throughout the outage for an emergency reason, please call us on 1300 463 933 during our <a href="/#contact">Customer Service Operating Hours</a> or email us at support@nexee.au. Please be aware that there may be long wait times before you are able to speak to us.</p>
            <p class="text-sm pt-0">Once the outage has been resolved, we will let you know via all the communication channels
              mentioned above.</p>
          </div>
        </div>
      </div>
      <div class="w-full flex lg:flex-row flex-col mt-6 pt-12">
        <div class="w-full lg:w-1/2 lg:pr-10">
          <p class="font-benzin mt-0 w-full max-w-4xl text-2xl uppercase">Current<br> Outages</p>
          <OutageList
            v-if="outageList && outageList.currentOutages && outageList.currentOutages.length > 0"
            :outages=outageList.currentOutages
          />
          <p class="sm:mt-2 max-w-xl text-lg text-gray-700 pt-2 font-poppins" v-else>
            No current outages.
          </p>
        </div>
        <div class="w-full lg:w-1/2 lg:pr-10">
          <p class="font-benzin mt-12 lg:mt-0 max-w-4xl text-2xl uppercase">Scheduled<br>Outages</p>
          <OutageList
            v-if="outageList && outageList.scheduledOutages && outageList.scheduledOutages.length > 0"
            :outages=outageList.scheduledOutages
          />
          <p class="sm:mt-2 max-w-xl text-lg text-gray-700 pt-2 font-poppins" v-else>
            No scheduled outages.
          </p>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import HeroText from '../components/content/HeroText.vue';
import Container from '../components/content/Container.vue';
import OutageList from '../components/content/OutageList.vue';
import store from '@/store';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/vue/outline';
import { computed, ref } from 'vue';

const showMajorOutagePolicy = ref(false);

const outageList = computed(() => {
  return store.state.outages;
});

const toggleMajorOutagePolicy = () => {
  showMajorOutagePolicy.value = !showMajorOutagePolicy.value;
};

</script>
<style scoped>
.bg-outage {
  background-repeat: no-repeat;
  background-color: transparent;
  background-image: url("https://uniti-bpo-website-assets.assets.unitiinternet.com/NEX/outage-hero-bg.png");
  background-position: center center;
  background-size: cover;
  height: 100%;
}
.bg-outage-note {
  background-repeat: no-repeat;
  background-color: transparent;
  background-image: url("https://uniti-bpo-website-assets.assets.unitiinternet.com/NEX/outage-note-bg.png");
  background-position: center center;
  background-size: cover;
  height: 100%;
}
</style>
